/**
 * BatchProgressBar Component
 *
 * Horizontal segmented bar visualizing batch progress by workflow stage.
 * Uses CSS variables for theming and smooth width transitions.
 *
 * Design:
 * - Segments scale to percentage of total items
 * - Minimum width (4px) ensures visibility for small counts
 * - Tooltip shows exact count on hover
 * - Completion percentage displayed at right end
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { BatchProgress, DominantStage } from '../types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BatchProgressBarProps {
  progress: BatchProgress;
  className?: string;
  showCompletionPercent?: boolean;
}

/**
 * Stage configuration mapping progress fields to display properties.
 * Order determines segment display order (left to right).
 */
const STAGE_CONFIG = [
  {
    key: 'pending' as const,
    labelKey: 'stages.pending',
    colorClass: 'bg-muted',
  },
  {
    key: 'picking' as const,
    labelKey: 'stages.picking',
    colorClass: 'bg-blue-400',
    countKeys: ['labelPrinted', 'picked'] as const,
  },
  {
    key: 'pretreat' as const,
    labelKey: 'stages.pretreat',
    colorClass: 'bg-purple-400',
    countKeys: ['inPretreat', 'pretreated'] as const,
  },
  // Phase 50.01.5 — DTF stages, between pretreat and catching (both DTG and DTF
  // converge at catching). Distinct, non-colliding tones: sky (film printed,
  // intermediate) + fuchsia (pressed, near-catching). DTG-only batches show
  // neither (counts are 0 → segment filtered out).
  {
    key: 'filmPrinted' as const,
    labelKey: 'stages.filmPrinted',
    colorClass: 'bg-sky-400',
    countKeys: ['filmPrinted'] as const,
  },
  {
    key: 'pressed' as const,
    labelKey: 'stages.pressed',
    colorClass: 'bg-fuchsia-400',
    countKeys: ['pressed'] as const,
  },
  {
    key: 'catching' as const,
    labelKey: 'stages.catching',
    colorClass: 'bg-amber-400',
    countKeys: ['caught'] as const,
  },
  {
    key: 'binning' as const,
    labelKey: 'stages.binning',
    colorClass: 'bg-orange-400',
    countKeys: ['inBin'] as const,
  },
  {
    key: 'packing' as const,
    labelKey: 'stages.packing',
    colorClass: 'bg-emerald-400',
    countKeys: ['readyToPack', 'released', 'packed'] as const,
  },
  {
    key: 'complete' as const,
    labelKey: 'stages.complete',
    colorClass: 'bg-green-600',
    countKeys: ['shipped'] as const,
  },
  {
    key: 'errors' as const,
    labelKey: 'stages.errors',
    colorClass: 'bg-destructive',
    countKeys: ['errors'] as const,
  },
] as const;

type CountKey = 'pending' | 'labelPrinted' | 'picked' | 'inPretreat' | 'pretreated' |
  'printed' | 'filmPrinted' | 'pressed' |
  'caught' | 'inBin' | 'readyToPack' | 'released' | 'packed' | 'shipped' | 'errors';

/**
 * Calculate count for a stage based on its count keys.
 * Uses countKeys array if defined, otherwise uses the key directly.
 */
function getStageCount(
  progress: BatchProgress,
  stage: typeof STAGE_CONFIG[number]
): number {
  if ('countKeys' in stage && stage.countKeys) {
    return stage.countKeys.reduce(
      (sum, key) => sum + (progress[key as CountKey] || 0),
      0
    );
  }
  return progress[stage.key as CountKey] || 0;
}

/**
 * BatchProgressBar - Segmented progress visualization
 */
export function BatchProgressBar({
  progress,
  className,
  showCompletionPercent = true,
}: BatchProgressBarProps) {
  const { t } = useTranslation('production');
  // Calculate segment widths as percentages
  const segments = useMemo(() => {
    const total = progress.total || 1; // Prevent division by zero
    return STAGE_CONFIG.map((stage) => {
      const count = getStageCount(progress, stage);
      const percent = (count / total) * 100;
      return {
        ...stage,
        count,
        percent,
        // Only show if count > 0
        visible: count > 0,
      };
    }).filter((s) => s.visible);
  }, [progress]);

  // Empty state
  if (progress.total === 0) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="h-4 flex-1 rounded-full bg-muted" />
        {showCompletionPercent && (
          <span className="text-xs text-muted-foreground font-medium">0%</span>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Progress bar container - h-4 for better warehouse visibility */}
      <div className="h-4 flex-1 rounded-full bg-muted overflow-hidden flex">
        <TooltipProvider>
          {segments.map((segment) => (
            <Tooltip key={segment.key}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'h-full transition-all duration-300',
                    segment.colorClass,
                    // First segment: rounded left
                    segments[0]?.key === segment.key && 'rounded-l-full',
                    // Last segment: rounded right
                    segments[segments.length - 1]?.key === segment.key && 'rounded-r-full'
                  )}
                  style={{
                    // Use flex-basis instead of width for smooth transitions
                    flexBasis: `${segment.percent}%`,
                    // Minimum width ensures visibility
                    minWidth: segment.count > 0 ? '4px' : '0',
                  }}
                  aria-label={`${t(segment.labelKey)}: ${t('batch.progress.stageTooltip', { count: segment.count, percent: segment.percent.toFixed(0) })}`}
                />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="font-medium">{t(segment.labelKey)}</p>
                <p className="text-xs text-muted-foreground">
                  {t('batch.progress.stageTooltip', { count: segment.count, percent: segment.percent.toFixed(0) })}
                </p>
              </TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </div>

      {/* Completion percentage */}
      {showCompletionPercent && (
        <span className="text-xs text-muted-foreground font-medium min-w-[32px] text-right">
          {progress.completionPercent}%
        </span>
      )}
    </div>
  );
}

/**
 * Get dominant stage label for display.
 * Accepts a t() function for i18n translation.
 */
export function getDominantStageLabel(stage: DominantStage, t: (key: string) => string): string {
  const config = STAGE_CONFIG.find((s) => s.key === stage);
  return config ? t(config.labelKey) : t('stages.unknown');
}

export default BatchProgressBar;
